import React, { ComponentProps } from 'react';

import PropTypes from 'prop-types';

import Button from '@adsk/alloy-react-button';
import Divider from '@adsk/alloy-react-divider';
import { useLocale } from '@adsk/alloy-react-locale';
import { stylePropType, StylableComponent } from '@adsk/alloy-react-theme';

import { GenericItem } from '../types';

export type ClearableFooterProps<TItem extends GenericItem> =
  StylableComponent<HTMLDivElement> & {
    setSelectedItems: (items: TItem[]) => void;
    clearButtonProps?: ComponentProps<typeof Button>;
  };

const DefaultClearButtonProps = {
  variant: Button.VARIANTS.TERTIARY,
  size: Button.SIZES.SMALL,
};

const ClearableFooter = <TItem extends GenericItem>({
  style,
  children,
  setSelectedItems,
  clearButtonProps,
  ...props
}: ClearableFooterProps<TItem>) => {
  const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));

  clearButtonProps = { ...DefaultClearButtonProps, ...clearButtonProps };

  return (
    <div css={[style]} {...props}>
      <Divider />
      <div
        css={{
          display: 'flex',
          justifyContent: children ? 'space-between' : 'right',
          padding: '12px 16px',
        }}
      >
        {children}
        <Button
          data-testid={'Dropdown__clear'}
          onClick={() => {
            setSelectedItems([]);
          }}
          {...clearButtonProps}
        >
          {T.translate('CLEAR')}
        </Button>
      </div>
    </div>
  );
};

ClearableFooter.displayName = 'ClearableFooter';

ClearableFooter.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Additional content, displayed to the left of the Clear button */
  children: PropTypes.node,
  /** Function used to set selected items to an empty array.  This is returned from the dropdown hook */
  setSelectedItems: PropTypes.func.isRequired,
  /** Optional props to set on the Clear button */
  clearButtonProps: PropTypes.object,
};

export default ClearableFooter;
